/**
 * Stagger reveal animations for child elements.
 *
 * Every target is observed on its own. Targets that enter the viewport in the same frame form one
 * batch and stagger by `data-stagger-delay`; targets below the fold wait until they are scrolled to.
 * The Web Animations API drives timing, so the browser composites the stagger without timers.
 *
 * A target starts at the first of two gates: a fraction of the target is visible (`threshold`, the
 * unit is the target, so it scales with the device), or the target's top crosses the start line
 * (`startLine`, a percent of the viewport, the net for targets taller than the fraction allows).
 * The gates are for what arrives by scrolling. Whatever is on screen when the revealer starts,
 * even by one pixel, starts at once: the reader is looking at that screen, and a band of hidden
 * cards under a heading reads as a broken page.
 */

import {shouldSaveData} from '../utils/networkInfo.js';
import {createRun} from '../modules/motion/run.js';
import {resolveStaggerOptions, staggerReveal} from '../modules/motion/staggerReveal.js';

/**
 * @typedef {object} RevealerOptions
 * @property {import('../modules/motion/staggerReveal.js').StaggerOptions|null} stagger How a batch animates; null shows the targets at once (mobile opt-out).
 * @property {number} threshold Visible fraction of a target that starts it.
 * @property {number} startLine Percent of the viewport height, from the bottom edge, whose crossing also starts it.
 * @property {string|null} targetSelector Selector for the targets; direct children when null.
 */

/**
 * @typedef {object} RevealerState
 * @property {IntersectionObserver[]} observers The two gates, both watching the pending targets.
 * @property {Set<HTMLElement>} pending Targets that did not start yet.
 * @property {HTMLElement[]} targets Every target in reveal order.
 * @property {import('../modules/motion/run.js').Run} run Every animation started so far.
 * @property {number} remaining Targets that have not finished animating yet.
 * @property {() => void} resizeStartLine Updates the start line after a viewport resize.
 */

/** @type {WeakMap<HTMLElement, RevealerState>} */
const stateMap = new WeakMap();

/** @type {boolean} */
const saveDataEnabled = shouldSaveData();

/**
 * The visible fraction is the main gate: with 40% of a card on screen the entrance is seen, and
 * the most that can ever sit blank at the fold is the rest of one card, on any screen size.
 */
const DEFAULT_THRESHOLD = 0.4;
/**
 * The start line is the net for targets taller than the fraction allows: whatever its height, a
 * target starts once its top is this far up from the bottom edge. Half the viewport is the ceiling:
 * a higher line would keep the last section of a page from ever starting.
 */
const DEFAULT_START_LINE_PERCENT = 25;
const MAX_START_LINE_PERCENT = 50;

/**
 * Hidden state between init and the moment a target's batch starts. The Blade component ships the
 * matching style rule. An attribute the revealer owns cannot collide with a target's own classes.
 */
const HIDDEN_ATTRIBUTE = 'data-stagger-hidden';

/**
 * @param {HTMLElement} container
 * @returns {RevealerOptions}
 */
function readOptions(container) {
    const {dataset} = container;

    let threshold = parseFloat(dataset.threshold || String(DEFAULT_THRESHOLD));
    if (Number.isNaN(threshold) || threshold < 0 || threshold > 1) {
        threshold = DEFAULT_THRESHOLD;
    }

    let startLine = parseInt(dataset.startLine || String(DEFAULT_START_LINE_PERCENT), 10);
    if (Number.isNaN(startLine)) {
        startLine = DEFAULT_START_LINE_PERCENT;
    }
    startLine = Math.max(0, Math.min(MAX_START_LINE_PERCENT, startLine));

    return {
        stagger: resolveStaggerOptions(dataset),
        threshold,
        startLine,
        targetSelector: dataset.targetSelector || null,
    };
}

/**
 * @param {HTMLElement} container
 * @param {string|null} targetSelector
 * @returns {HTMLElement[]}
 */
function getAnimationTargets(container, targetSelector) {
    const candidates = targetSelector ? container.querySelectorAll(targetSelector) : container.children;
    const targets = [...candidates].filter((element) => element instanceof HTMLElement);

    if (container.dataset.respectCssOrder === 'true' && targets.length > 1) {
        /** @type {Map<HTMLElement, number>} */
        const orderCache = new Map();
        for (const target of targets) {
            orderCache.set(target, parseInt(getComputedStyle(target).order, 10) || 0);
        }
        targets.sort((a, b) => (orderCache.get(a) ?? 0) - (orderCache.get(b) ?? 0));
    }

    return targets;
}

/**
 * Animate one batch of targets that entered the viewport together.
 * @param {HTMLElement} container
 * @param {RevealerState} state
 * @param {HTMLElement[]} batch Targets in reveal order.
 * @param {RevealerOptions} options
 */
function revealBatch(container, state, batch, {stagger}) {
    if (stagger === null) {
        throw new Error('Stagger revealer: a container without a reveal must not batch.');
    }
    container.dataset.animationState = 'animating';

    // Same task as the animation start, so the attribute removal and the first keyframe land in one style recalc.
    for (const target of batch) {
        target.removeAttribute(HIDDEN_ATTRIBUTE);
    }

    state.run.add(...staggerReveal(batch, stagger, () => {
        state.remaining -= batch.length;

        if (state.remaining === 0) {
            for (const observer of state.observers) {
                observer.disconnect();
            }
            container.dataset.animationState = 'complete';
        }
    }, container));
}

/**
 * @param {HTMLElement} container
 */
function initializeStaggerRevealer(container) {
    if (stateMap.has(container)) {
        return;
    }

    if (saveDataEnabled) {
        container.dataset.animationState = 'complete';
        return;
    }

    const options = readOptions(container);
    const targets = getAnimationTargets(container, options.targetSelector);

    if (targets.length === 0 || options.stagger === null) {
        container.dataset.animationState = 'complete';
        return;
    }

    /** @type {Map<HTMLElement, number>} */
    const revealOrder = new Map(targets.map((target, index) => [target, index]));

    /* The fraction gate's first report carries every target's real ratio, thresholds aside. That
       report is the "already on screen" pass: any visible part counts, once. */
    let firstReport = true;

    /**
     * Either gate reports; a target starts once, at the first report.
     * @param {IntersectionObserverEntry[]} entries
     * @param {IntersectionObserver} observer
     */
    function onGate(entries, observer) {
        const state = stateMap.get(container);
        if (!state || !state.observers.includes(observer)) {
            return;
        }

        const isInitialPass = firstReport && observer === state.observers[0];
        firstReport = firstReport && !isInitialPass;
        const passes = (/** @type {IntersectionObserverEntry} */ entry) => {
            if (isInitialPass) {
                return entry.intersectionRatio > 0;
            }

            return entry.isIntersecting && (observer !== state.observers[0] || entry.intersectionRatio >= options.threshold);
        };

        const batch = [...new Set(entries
            .filter((entry) => passes(entry) && state.pending.has(/** @type {HTMLElement} */ (entry.target)))
            .map((entry) => /** @type {HTMLElement} */ (entry.target)))]
            .sort((a, b) => (revealOrder.get(a) ?? 0) - (revealOrder.get(b) ?? 0));

        if (batch.length === 0) {
            return;
        }

        for (const target of batch) {
            state.pending.delete(target);
            for (const observer of state.observers) {
                observer.unobserve(target);
            }
        }

        revealBatch(container, state, batch, options);
    }

    let viewportHeight = window.innerHeight;
    const createStartLineObserver = () => new IntersectionObserver(onGate, {
        threshold: 0,
        // Percentage root margins use width, even on the bottom edge.
        rootMargin: `0px 0px -${viewportHeight * options.startLine / 100}px 0px`,
    });
    const observers = [
        // The fraction gate: the unit is the target itself.
        new IntersectionObserver(onGate, {threshold: options.threshold}),
        // The start line gate: a negative bottom margin moves the line up from the viewport's bottom edge.
        createStartLineObserver(),
    ];

    const pending = new Set(targets);
    const resizeStartLine = () => {
        if (viewportHeight === window.innerHeight || pending.size === 0) {
            return;
        }
        viewportHeight = window.innerHeight;
        observers[1].disconnect();
        observers[1] = createStartLineObserver();
        for (const target of pending) {
            observers[1].observe(target);
        }
    };
    stateMap.set(container, {observers, pending, targets, run: createRun(), remaining: targets.length, resizeStartLine});

    for (const target of targets) {
        target.setAttribute(HIDDEN_ATTRIBUTE, '');
        for (const observer of observers) {
            observer.observe(target);
        }
    }

    container.dataset.animationState = 'pending';
}

/**
 * Stop observing and animating, and show every target.
 * @param {HTMLElement} container
 */
export function cleanupStaggerRevealer(container) {
    const state = stateMap.get(container);
    if (!state) {
        return;
    }

    for (const observer of state.observers) {
        observer.disconnect();
    }
    state.run.cancel();
    for (const target of state.targets) {
        target.removeAttribute(HIDDEN_ATTRIBUTE);
    }

    stateMap.delete(container);
    delete container.dataset.animationState;
}

/**
 * Initialize every stagger revealer that is not initialized yet. Safe to call after content swaps.
 */
export function initializeComponent() {
    for (const container of document.querySelectorAll('.js-staggerRevealer')) {
        if (container instanceof HTMLElement) {
            initializeStaggerRevealer(container);
        }
    }
}

/**
 * Replay every stagger revealer from its hidden state.
 */
export function reinitializeStaggerRevealers() {
    for (const container of document.querySelectorAll('.js-staggerRevealer')) {
        if (container instanceof HTMLElement) {
            cleanupStaggerRevealer(container);
        }
    }

    initializeComponent();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeComponent);
} else {
    initializeComponent();
}

document.addEventListener('staggerRevealer:replay', (/** @type {CustomEvent} */ event) => {
    const {container} = event.detail || {};

    if (container instanceof HTMLElement) {
        cleanupStaggerRevealer(container);
        initializeStaggerRevealer(container);
    } else {
        reinitializeStaggerRevealers();
    }
});

window.addEventListener('resize', () => {
    for (const container of document.querySelectorAll('.js-staggerRevealer')) {
        stateMap.get(/** @type {HTMLElement} */ (container))?.resizeStartLine();
    }
});

document.addEventListener('htmx:load', initializeComponent);
document.addEventListener('htmx:beforeCleanupElement', (event) => {
    if (event.target instanceof HTMLElement) {
        cleanupStaggerRevealer(event.target);
    }
});
